/**
 * Feature-local crew + activity-picker API for the Planner Crew page.
 *
 * Owned by Agent B (Wave 8). Kept in a sibling file (`crew.ts`) rather than
 * `queries.ts` / `mutations.ts` so Agent A's foundation queries can ship in
 * parallel without touching the same file.
 *
 * Endpoints used:
 *   - GET /User/TenantUser/FieldUser?tenantId=X&pageNumber=1&pageSize=200
 *   - GET /Planner/crew-load?date=YYYY-MM-DD
 *   - GET /Planner/cards?from=X&to=Y          (booked activities for grid cells)
 *   - GET /Planner/{activityId}/available-work-order-tasks  (picker step 2)
 *   - POST /Planner/{id}/crew-member          (quick-assign from grid cell)
 *
 * NOTE: A dedicated `/CrewMember/Assign` endpoint is not exposed today. We
 * reuse the existing PATCH `/Planner/{id}/crew-member` (legacy
 * `addSingleCrewMember`) and surface the UI as "Quick assign" — the user picks
 * an existing PlannerActivity for that day and the worker is appended.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { APIClient } from '@/helpers/api_helper';
import * as url from '@/helpers/url_helper';
import { useCurrentUser } from '@/shared/auth';

import type {
  ActivityPickerOption,
  CrewAvailability,
  CrewRosterMember,
  ScaffoldTaskPickerOption,
} from '../types/crew';
import type {
  CrewAvailabilityStatus,
  CrewSkill,
} from '../schemas/crew.schema';
import { plannerKeys } from './keys';

const api = new APIClient();

/**
 * Query key factory for crew-page reads. Layered on top of Agent A's
 * `plannerKeys` factory — the roster, availability matrix, and the picker
 * dropdowns each hang off the reservations Agent A already left
 * (`plannerKeys.crewAssignments`, `plannerKeys.crewAvailability`).
 */
export const plannerCrewKeys = {
  members: () => [...plannerKeys.all, 'crew-roster'] as const,
  membersList: (filters?: { tenantId?: string }) =>
    [...plannerCrewKeys.members(), filters ?? {}] as const,
  availability: () => plannerKeys.crewAvailability(),
  availabilityWeek: (weekStart: string, weekEnd: string) =>
    [...plannerCrewKeys.availability(), { weekStart, weekEnd }] as const,
  pickerActivities: (filters: {
    from: string;
    to: string;
    clientId?: string;
    scaffoldId?: string;
  }) => [...plannerKeys.all, 'picker', 'activities', filters] as const,
  pickerTasks: (activityId: string) =>
    [...plannerKeys.all, 'picker', 'tasks', activityId] as const,
} as const;

/* -------------------------------------------------------------------------- */
/* Crew members                                                               */
/* -------------------------------------------------------------------------- */

/** Backend payload — only the fields we read. */
interface FieldUserApiResponse {
  items?: Array<{
    id?: string;
    userId?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    name?: string;
    roleId?: string;
    roleName?: string;
    departmentId?: string;
    departmentName?: string;
    tenantLocationId?: string;
    tenantLocationName?: string;
    avatarUrl?: string;
    isDisabled?: boolean;
    isDeleted?: boolean;
  }>;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Map a role / department string to the skill chips we surface in the UI.
 * Today this is heuristic-based — the backend doesn't expose a structured
 * `skills` array yet. Conservative defaults so the chip filter is useful even
 * before the data shape lands.
 */
function inferSkills(roleName?: string, departmentName?: string): CrewSkill[] {
  const haystack = `${roleName ?? ''} ${departmentName ?? ''}`.toLowerCase();
  if (!haystack.trim()) return ['General'];

  const skills = new Set<CrewSkill>();
  if (haystack.includes('all') || haystack.includes('rounder')) {
    skills.add('AllRounder');
  }
  if (haystack.includes('erect') || haystack.includes('rigger')) {
    skills.add('Erect');
  }
  if (haystack.includes('modify')) skills.add('Modify');
  if (haystack.includes('dismantle') || haystack.includes('strike')) {
    skills.add('Dismantle');
  }
  if (haystack.includes('inspect')) skills.add('Inspection');
  if (haystack.includes('maintain') || haystack.includes('maintenance')) {
    skills.add('Maintenance');
  }
  if (skills.size === 0) skills.add('General');
  return Array.from(skills);
}

function normalizeCrewMember(
  raw: NonNullable<FieldUserApiResponse['items']>[number],
): CrewRosterMember | null {
  const id = raw.id ?? raw.userId;
  if (!id) return null;
  if (raw.isDisabled || raw.isDeleted) return null;
  const fullName =
    raw.fullName ??
    raw.name ??
    [raw.firstName, raw.lastName].filter(Boolean).join(' ') ??
    'Unnamed worker';
  return {
    id,
    fullName: fullName.trim() || 'Unnamed worker',
    initials: getInitials(fullName || 'NA'),
    roleName: raw.roleName,
    skills: inferSkills(raw.roleName, raw.departmentName),
    tenantLocationId: raw.tenantLocationId,
    tenantLocationName: raw.tenantLocationName,
    avatarUrl: raw.avatarUrl,
    disabled: raw.isDisabled,
  };
}

/**
 * List of field workers (CrewMembers) for the active tenant.
 *
 * Hits `/User/TenantUser/FieldUser?tenantId=…` — the same endpoint legacy
 * planner uses for its crew pool. Returns the normalised `CrewRosterMember` shape
 * the page consumes.
 */
export function useCrewMembersList() {
  const currentUser = useCurrentUser();
  const tenantId = currentUser?.tenantId;

  return useQuery({
    queryKey: plannerCrewKeys.membersList({ tenantId: tenantId ?? '' }),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const params: Record<string, string | number> = {
        pageNumber: 1,
        pageSize: 200,
      };
      if (tenantId) params.tenantId = tenantId;
      const response = await api.get<FieldUserApiResponse>(
        url.GET_TENANT_FIELD_USERS,
        params,
      );
      const raw = response?.items ?? [];
      return raw
        .map(normalizeCrewMember)
        .filter((m): m is CrewRosterMember => m !== null);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/* -------------------------------------------------------------------------- */
/* Availability                                                               */
/* -------------------------------------------------------------------------- */

interface PlannerCardCrewSlot {
  userId?: string;
  crewMemberId?: string;
  id?: string;
}

interface PlannerCardLite {
  id?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  crew?: PlannerCardCrewSlot[];
  crewMembers?: PlannerCardCrewSlot[];
}

interface PlannerCardsResponse {
  items?: PlannerCardLite[];
  cards?: PlannerCardLite[];
  result?: PlannerCardLite[];
}

function toArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const arr = obj.items ?? obj.cards ?? obj.result ?? obj.data;
    if (Array.isArray(arr)) return arr as T[];
  }
  return [];
}

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

function isWeekend(isoDate: string): boolean {
  const d = new Date(`${isoDate}T00:00:00`);
  const day = d.getDay();
  return day === 0 || day === 6;
}

/**
 * Build the (crewMemberId, date) availability matrix for the visible week.
 *
 * Pulls planner cards for the week range, then for each crew member × day
 * computes:
 *   - `Booked` if the worker appears on any card whose scheduledStart falls
 *     on that day,
 *   - `Off` for weekends,
 *   - `Available` otherwise.
 *
 * `OnLeave` is reserved for when the backend exposes leave records (TODO).
 */
export function useCrewAvailability(weekStart: string, weekEnd: string) {
  return useQuery({
    queryKey: plannerCrewKeys.availabilityWeek(weekStart, weekEnd),
    enabled: Boolean(weekStart && weekEnd),
    queryFn: async (): Promise<CrewAvailability[]> => {
      const response = await api.get<PlannerCardsResponse>(
        url.GET_PLANNER_CARDS,
        { from: weekStart, to: weekEnd },
      );
      const cards = toArray<PlannerCardLite>(response);
      const rows: CrewAvailability[] = [];

      for (const card of cards) {
        if (!card.scheduledStart) continue;
        const date = dayKey(card.scheduledStart);
        const crewArr = card.crew ?? card.crewMembers ?? [];
        for (const c of crewArr) {
          const userId = c.userId ?? c.crewMemberId ?? c.id;
          if (!userId) continue;
          rows.push({
            crewMemberId: userId,
            date,
            status: 'Booked',
            activityIds: card.id ? [card.id] : [],
          });
        }
      }
      return rows;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Resolve the availability status for a (crew member, day) pair given the
 * full availability matrix. Pure helper — components can call this for each
 * grid cell without a re-query.
 */
export function resolveAvailabilityStatus(
  crewMemberId: string,
  date: string,
  matrix: CrewAvailability[] | undefined,
): { status: CrewAvailabilityStatus; activityIds: string[] } {
  const hits = (matrix ?? []).filter(
    (row) => row.crewMemberId === crewMemberId && row.date === date,
  );
  if (hits.length > 0) {
    const onLeave = hits.find((h) => h.status === 'OnLeave');
    if (onLeave) {
      return { status: 'OnLeave', activityIds: onLeave.activityIds };
    }
    return {
      status: 'Booked',
      activityIds: hits.flatMap((h) => h.activityIds),
    };
  }
  if (isWeekend(date)) {
    return { status: 'Off', activityIds: [] };
  }
  return { status: 'Available', activityIds: [] };
}

/* -------------------------------------------------------------------------- */
/* Activity-Task picker                                                       */
/* -------------------------------------------------------------------------- */

interface PlannerActivityPickerCard {
  id?: string;
  activityNumber?: string;
  title?: string;
  status?: number;
  actionIntent?: number;
  actionIntentName?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  clientName?: string;
  siteName?: string;
  jobNumber?: string;
  taskCount?: number;
  clientId?: string;
  scaffoldId?: string;
}

function normalizeActivityOption(
  raw: PlannerActivityPickerCard,
): ActivityPickerOption | null {
  if (!raw.id || !raw.scheduledStart) return null;
  return {
    id: raw.id,
    activityNumber: raw.activityNumber,
    title: raw.title ?? raw.activityNumber ?? 'Untitled activity',
    status: raw.status ?? 0,
    actionIntent: raw.actionIntent ?? 0,
    actionIntentName: raw.actionIntentName,
    scheduledStart: raw.scheduledStart,
    scheduledEnd: raw.scheduledEnd,
    clientName: raw.clientName,
    siteName: raw.siteName,
    jobNumber: raw.jobNumber,
    taskCount: raw.taskCount,
  };
}

/**
 * Activities the picker step 1 can choose from. Defaults to a 30-day window
 * starting today; the page can pass a tighter range if needed.
 *
 * `clientId` and `scaffoldId` are forwarded to the backend when set so the
 * cascading dropdowns the brief asks for work without an extra round-trip.
 */
export function useActivityPickerOptions(filters: {
  from: string;
  to: string;
  clientId?: string;
  scaffoldId?: string;
  enabled?: boolean;
}) {
  const { from, to, clientId, scaffoldId, enabled = true } = filters;
  return useQuery({
    queryKey: plannerCrewKeys.pickerActivities({
      from,
      to,
      clientId,
      scaffoldId,
    }),
    enabled: enabled && Boolean(from && to),
    queryFn: async () => {
      const params: Record<string, string> = { from, to };
      if (clientId) params.clientId = clientId;
      if (scaffoldId) params.scaffoldId = scaffoldId;
      const response = await api.get<unknown>(url.GET_PLANNER_CARDS, params);
      const cards = toArray<PlannerActivityPickerCard>(response);
      return cards
        .map(normalizeActivityOption)
        .filter((c): c is ActivityPickerOption => c !== null);
    },
    staleTime: 60 * 1000,
  });
}

interface AvailableTaskApi {
  id?: string;
  taskNumber?: string;
  taskDescription?: string;
  scaffoldId?: string;
  scaffoldNumber?: string;
  scaffoldName?: string;
  scaffoldTaskType?: number;
  scaffoldTaskTypeName?: string;
  hasAssignments?: boolean;
}

const TASK_TYPE_LABEL: Record<number, string> = {
  1: 'Erect',
  2: 'Modify',
  3: 'Dismantle',
  4: 'Inspection',
  5: 'Maintenance',
  6: 'General',
};

function normalizeTaskOption(
  raw: AvailableTaskApi,
): ScaffoldTaskPickerOption | null {
  if (!raw.id) return null;
  const taskType = raw.scaffoldTaskType ?? 6;
  return {
    id: raw.id,
    taskNumber: raw.taskNumber,
    taskDescription:
      raw.taskDescription ?? raw.taskNumber ?? 'Untitled task',
    scaffoldId: raw.scaffoldId,
    scaffoldNumber: raw.scaffoldNumber,
    scaffoldName: raw.scaffoldName,
    scaffoldTaskType: taskType,
    scaffoldTaskTypeName: raw.scaffoldTaskTypeName ?? TASK_TYPE_LABEL[taskType] ?? 'General',
    hasAssignments: Boolean(raw.hasAssignments),
  };
}

/**
 * Tasks for the picker step 2 — driven off the existing planner endpoint
 * that returns only Structure / Custom tasks (filtered server-side).
 */
export function useScaffoldTaskPickerOptions(
  activityId: string | null,
  options?: Omit<UseQueryOptions<ScaffoldTaskPickerOption[]>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery({
    queryKey: plannerCrewKeys.pickerTasks(activityId ?? ''),
    enabled: Boolean(activityId),
    queryFn: async () => {
      const response = await api.get<unknown>(
        `${url.GET_PLANNER_ACTIVITY}/${activityId}/available-work-order-tasks`,
      );
      const tasks = toArray<AvailableTaskApi>(response);
      return tasks
        .map(normalizeTaskOption)
        .filter((t): t is ScaffoldTaskPickerOption => t !== null);
    },
    staleTime: 30 * 1000,
    ...options,
  });
}

/* -------------------------------------------------------------------------- */
/* Mutations                                                                  */
/* -------------------------------------------------------------------------- */

interface QuickAssignPayload {
  activityId: string;
  crewMemberId: string;
}

/**
 * Append a crew member to an existing PlannerActivity. Wraps the legacy
 * `PATCH /Planner/{id}/crew-member` endpoint and invalidates the availability
 * matrix so the grid cell reflects the new booking.
 */
export function useQuickAssignCrewMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ activityId, crewMemberId }: QuickAssignPayload) => {
      const response = await api.update<unknown>(
        `${url.PATCH_PLANNER_CREW_MEMBER}/${activityId}/crew-member`,
        { crewMemberId },
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: plannerCrewKeys.availability(),
      });
      // Keep the daily grid + activity lists in sync — the quick-assign also
      // mutates the activity's crew set, which the Planner grid renders.
      queryClient.invalidateQueries({ queryKey: plannerKeys.activities() });
      queryClient.invalidateQueries({
        queryKey: plannerKeys.schedulingGrids(),
      });
    },
  });
}
